import { Close } from '@ahamove/icons';
import {
  Box,
  type ButtonProps,
  Drawer,
  Modal,
  rem,
  Title,
  Group,
  ActionIcon,
} from '@mantine/core';
import React, { type CSSProperties, type FC, type ReactNode } from 'react';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

interface CustomComponentProps {
  title?: string;
  modalId?: string;
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  optionStyle?: {
    titleStyle?: CSSProperties;
    bodyStyle?: CSSProperties;
    headerStyle?: CSSProperties;
    contentStyle?: CSSProperties;
  };
  closeButtonProps?: ButtonProps;
  rigthIconTitle?: ReactNode;
  onClickRigthIcon?: () => void;
}

const CustomComponent: FC<CustomComponentProps> = ({
  title,
  children,
  isOpen,
  modalId,
  onClose,
  optionStyle = {},
  closeButtonProps = {},
  rigthIconTitle,
  onClickRigthIcon,
}) => {
  const { titleStyle, bodyStyle, headerStyle, contentStyle } = optionStyle;
  const isMobile = useIsMobile();

  const handleClickRigthIcon = () => {
    if (onClickRigthIcon) {
      onClickRigthIcon();
    }
  };

  if (isMobile) {
    return (
      <Drawer
        zIndex={999}
        opened={isOpen}
        onClose={onClose}
        position="bottom"
        title={
          <Group justify="center" align="center">
            <Box w="80%">
              <Title
                order={5}
                lineClamp={1}
                ta="center"
                ml={rigthIconTitle ? rem(24) : 0}
              >
                {title}
              </Title>
            </Box>
            {rigthIconTitle ? (
              <ActionIcon variant="subtle" onClick={handleClickRigthIcon}>
                {rigthIconTitle}
              </ActionIcon>
            ) : null}
          </Group>
        }
        left={100}
        closeButtonProps={{
          pos: 'absolute',
          left: rem(12),
          ...closeButtonProps,
        }}
        // scrollAreaComponent={ScrollArea.Autosize}
        closeOnClickOutside={false}
        styles={{
          close: {
            '--cb-icon-size': rem(24),
          },
          title: {
            flex: 1,
            textAlign: 'center',
            fontSize: 18,
            lineHeight: '24px',
            fontWeight: 700,
            ...titleStyle,
          },
          header: {
            paddingTop: rem(12),
            paddingBottom: rem(12),
            borderBottom: '1px solid var(--mantine-color-neutral-0)',
            minHeight: rem(56),
            ...headerStyle,
          },
          body: {
            height: 'fit-content',
            position: 'relative',
            backgroundColor: 'white',
            padding: rem(16),
            ...bodyStyle,
          },
          content: {
            maxHeight: '95svh',
            height: 'fit-content',
            borderTopLeftRadius: rem(14),
            borderTopRightRadius: rem(14),
            ...contentStyle,
          },
        }}
      >
        {children}
      </Drawer>
    );
  }

  return (
    <Modal
      id={modalId}
      closeOnClickOutside={false}
      centered
      opened={isOpen}
      onClose={onClose}
      title={title}
      closeButtonProps={{
        ...closeButtonProps,
        mr: rem(-8),
        radius: 'xl',
        icon: <Close width={22} height={22} />,
      }}
      styles={{
        title: {
          fontSize: 20,
          fontWeight: 700,
          ...titleStyle,
        },
        header: {
          height: rem(56),
          backgroundColor: 'white',
          paddingLeft: rem(24),
          paddingRight: rem(24),
          ...headerStyle,
        },
        content: {
          height: 'fit-content',
          backgroundColor: 'var(--mantine-color-neutral-0)',
          ...contentStyle,
        },
        body: {
          height: 'fit-content',
          position: 'relative',
          backgroundColor: 'white',
          paddingLeft: rem(24),
          paddingRight: rem(24),
          paddingBottom: rem(24),
          ...bodyStyle,
        },
      }}
    >
      {children}
    </Modal>
  );
};

export default CustomComponent;
